// Compare page — loading skeletons exactly per frames 1n (desktop) and 1o
// (mobile): the structure stands immediately, pulse placeholders fill in.

const SKEL_COLS = [
  { w1: "78%", w2: "56%", delay: "0s" },
  { w1: "66%", w2: "62%", delay: ".18s" },
  { w1: "72%", w2: "58%", delay: ".3s" },
  { w1: "60%", w2: "64%", delay: ".42s" },
];

const SKEL_ROWS = [
  ["58%", "72%", "60%"],
  ["44%", "58%", "70%"],
  ["66%", "64%", "52%"],
  ["52%", "76%", "62%"],
  ["48%", "56%", "68%"],
  ["62%", "68%", "58%"],
];

// animate-pulse comes from Tailwind; only the stagger delay is inline.
const pulse = (delay) => ({ animationDelay: delay });

export const CompareSkeletonDesktop = ({ playerCount = 2 }) => {
  const n = Math.max(2, Math.min(4, playerCount));
  const compact = n > 2;
  const cols = `minmax(0,${compact ? "1.02" : "1.28"}fr) repeat(${n}, minmax(0,1fr))`;
  const colDefs = SKEL_COLS.slice(0, n);
  return (
    <div className="bg-white border border-[#E4E9F0] rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,.05),0_22px_44px_-26px_rgba(16,24,40,.28)]">
      <div className="flex items-center justify-between px-[18px] py-[14px]">
        <div className="flex flex-col gap-[6px]">
          <div className="text-[17px] font-bold tracking-[-.01em] text-[#0F172A]">Statistics</div>
          <div className="flex items-center gap-2">
            <div className="w-[11px] h-[11px] rounded-full border-2 border-[#DCE5F0] border-t-[#2283FF] animate-spin" />
            <span className="text-[12.5px] text-[#5A6879]">Loading season statistics...</span>
          </div>
        </div>
        <div className="w-[212px] h-[38px] rounded-[9px] bg-[#EDF1F7] animate-pulse" style={pulse("0s")} />
      </div>
      <div className="grid items-end px-[18px] pt-4 pb-[14px] border-t border-[#E9EEF5]" style={{ gridTemplateColumns: cols }}>
        <div className="w-[76px] h-[11px] rounded bg-[#EDF1F7]" />
        {colDefs.map((c, i) => (
          <div key={i} className="flex flex-col gap-[9px] pl-[14px]">
            <div className="flex items-center gap-[11px]">
              <div className="w-[46px] h-[46px] rounded-full bg-[#EDF1F7] animate-pulse" style={pulse(c.delay)} />
              <div className="flex flex-col gap-[7px] flex-1">
                <div className="h-[14px] rounded-[5px] bg-[#EDF1F7] animate-pulse" style={{ width: c.w1, ...pulse(c.delay) }} />
                <div className="h-[10px] rounded bg-[#F2F5F9] animate-pulse" style={{ width: c.w2, ...pulse(c.delay) }} />
              </div>
            </div>
            <div className="flex gap-[5px]">
              <div className="w-[38px] h-[18px] rounded-md bg-[#EAF3FF]" />
              <div className="w-[52px] h-[18px] rounded-md bg-[#F2F5F9]" />
              <div className="w-[44px] h-[18px] rounded-md bg-[#F2F5F9]" />
            </div>
          </div>
        ))}
      </div>
      <div className="grid items-center px-[18px] py-4 bg-[#F3F8FF] border-t border-[#E1EBFA]" style={{ gridTemplateColumns: cols }}>
        <div className="flex flex-col gap-1">
          <div className="text-[15px] font-bold text-[#0F172A]">Rating</div>
          <div className="text-[11.5px] text-[#7A8798]">Ø per match</div>
        </div>
        {colDefs.map((c, i) => (
          <div key={i} className="ml-[14px] flex flex-col gap-[9px] items-end">
            <div className="w-[92px] h-[34px] rounded-lg bg-[#E3ECF9] animate-pulse" style={pulse(c.delay)} />
            <div className="w-full h-[6px] rounded-[3px] bg-[#E3ECF9]" />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between px-[18px] py-[11px] bg-[#1B6FD6]">
        <span className="text-[11.5px] font-bold tracking-[.1em] uppercase text-white">Overview</span>
      </div>
      {SKEL_ROWS.map((r, i) => (
        <div key={i} className="grid items-center px-[18px] py-3 border-t border-[#F1F5F9]" style={{ gridTemplateColumns: cols }}>
          <div className="h-3 rounded bg-[#EDF1F7] animate-pulse" style={{ width: r[0], ...pulse(`${(i * 0.07).toFixed(2)}s`) }} />
          {colDefs.map((c, j) => (
            <div key={j} className="ml-[14px] flex flex-col gap-[6px] items-end">
              <div
                className="h-[15px] rounded-[5px] bg-[#EDF1F7] animate-pulse"
                style={{ width: r[1 + (j % 2)], ...pulse(`${(i * 0.07 + j * 0.06).toFixed(2)}s`) }}
              />
              <div className="w-full h-1 rounded-sm bg-[#F3F6FA]" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const CompareSkeletonMobile = ({ playerCount = 2 }) => {
  const n = Math.max(2, Math.min(4, playerCount));
  const cols = `repeat(${n}, minmax(0,1fr))`;
  const colDefs = SKEL_COLS.slice(0, n);
  return (
    <div className="bg-[#F5F7FA]">
      <div className="bg-white border-b border-[#E4E9F0] p-3">
        <div className="flex items-center justify-between mb-[9px]">
          <div className="text-[16px] font-bold tracking-[-.01em] text-[#0F172A]">Compare</div>
          <div className="flex items-center gap-[7px]">
            <div className="w-[11px] h-[11px] rounded-full border-2 border-[#DCE5F0] border-t-[#2283FF] animate-spin" />
            <span className="text-[11px] text-[#7A8798]">Loading...</span>
          </div>
        </div>
        <div className="flex gap-[7px]">
          {colDefs.slice(0, Math.min(n, 2)).map((c, i) => (
            <div key={i} className="flex items-center gap-2 pl-[6px] pr-[10px] py-[6px] border border-[#E7ECF3] rounded-[11px] bg-white flex-1">
              <div className="w-[30px] h-[30px] rounded-full bg-[#EDF1F7] animate-pulse" style={pulse(c.delay)} />
              <div className="flex flex-col gap-[5px] flex-1">
                <div className="h-[11px] rounded bg-[#EDF1F7]" style={{ width: c.w1 }} />
                <div className="h-2 rounded-[3px] bg-[#F2F5F9]" style={{ width: c.w2 }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="p-3 flex flex-col gap-[10px]">
        <div className="h-[56px] rounded-xl bg-white border border-[#E4E9F0]" />
        <div className="bg-white border border-[#E4E9F0] rounded-[14px] p-[13px] flex flex-col gap-[10px]">
          <div className="flex items-center justify-between">
            <span className="text-[14.5px] font-bold text-[#0F172A]">Rating</span>
            <span className="text-[11.5px] text-[#7A8798]">Ø per match</span>
          </div>
          <div className="grid gap-[7px]" style={{ gridTemplateColumns: cols }}>
            {colDefs.map((c, i) => (
              <div key={i} className="p-[10px] rounded-[10px] bg-[#F3F8FF] flex flex-col gap-2 items-center">
                <div className="w-[56px] h-[26px] rounded-[7px] bg-[#E3ECF9] animate-pulse" style={pulse(c.delay)} />
                <div className="w-full h-[5px] rounded-[3px] bg-[#E3ECF9]" />
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#E4E9F0] rounded-[14px] overflow-hidden">
          <div className="px-[13px] py-[11px] bg-[#1B6FD6]">
            <span className="text-[11px] font-bold tracking-[.1em] uppercase text-white">Overview</span>
          </div>
          {SKEL_ROWS.map((r, i) => (
            <div key={i} className="px-[13px] py-[11px] border-b border-[#F4F7FA] last:border-b-0">
              <div
                className="h-[11px] rounded bg-[#EDF1F7] mb-[9px] animate-pulse"
                style={{ width: r[0], ...pulse(`${(i * 0.07).toFixed(2)}s`) }}
              />
              <div className="grid gap-[7px]" style={{ gridTemplateColumns: cols }}>
                {colDefs.map((c, j) => (
                  <div key={j} className="flex flex-col gap-[6px] items-center">
                    <div
                      className="h-[14px] rounded-[5px] bg-[#EDF1F7] animate-pulse"
                      style={{ width: r[1 + (j % 2)], ...pulse(`${(i * 0.07 + j * 0.06).toFixed(2)}s`) }}
                    />
                    <div className="w-full h-1 rounded-sm bg-[#F3F6FA]" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
